import { useRef, useEffect, useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { InfoBox, Marker } from '@react-google-maps/api';
import BlockUi from 'js/components/blockUi/BlockUi';
import useMarkerIcon from 'js/hooks/useMarkerIcon';
import ScrollableContainer from 'js/components/common/ScrollableContainer';
import BubbleContent, { Container } from 'js/components/searchResults/views/map/BubbleContent';
import SearchResultsFunctions from 'js/helpers/search/SearchResultsFunctions';
import BubbleContentSummary from 'js/components/searchResults/views/map/BubbleContentSummary';
import { useSearchSelectContext } from 'js/components/higherOrderComponents/search/SearchResultsWithItemSelection';
import InfoWindowCloseButton from './InfoWindowCloseButton';

const Content = (props) => {
    const properties = useSelector(state => state
        .searchResults
        .get('map')
        .get('dynamic')
        ?.properties, shallowEqual);

    const isInBoundary = (property, coords) => {
        if (!property.addressLocation || !coords) return false;
        try {
            const { localityBounds, shapeBounds } = props.getLocalityBoundary();
            const isInLocality = !!localityBounds ?
                window.google.maps.geometry.poly.containsLocation(coords, localityBounds) : false;
            const isInShape = shapeBounds?.flat()
                ?.some(boundaryPart => window.google.maps.geometry.poly.containsLocation(coords,
                    new window.google.maps.Polygon({ paths: boundaryPart }))
            ) ?? false;
            const isInLine = Object.entries(props.getIncludedIds())
                .some(([_key, _value]) => {
                    if (_key.startsWith('polyline')) {
                        return _value.includes(property.id);
                    }
                    return false;
                });
            return isInLocality || isInShape || isInLine;
        } catch (e) {
            console.error('Error checking the address location:', e);
            return false;
        }
    };

    return (
        <Container sx={{
            minHeight: '101px',
        }}>
            <BlockUi
                blocking={!properties?.length}
                renderChildren={false}
                containerProps={{
                    testId: 'map-content-loader',
                    className: 'white-blocker',

                }}
                backdropProps={{
                    style: {
                        height: '100%'
                    }
                }}
            >
                <>
                    <ScrollableContainer
                        allowOuterScroll
                        keepAtBottom
                        width="500px"
                        style={{
                            minHeight: '101px',
                            maxHeight: '306px',
                        }}
                    >
                        {
                            properties?.map(property => {

                                return (<BubbleContent
                                    // only show checkbox if property is within locality boundary
                                    hideSelectCheckbox={!isInBoundary(property, new window.google.maps.LatLng(property.addressLocation?.lat, property.addressLocation?.lon))}
                                    error={!!property.error}
                                    noData={!!property.noResults}
                                    key={`search-result-map-dynamic-property-bubble-${property.id}`}
                                    id={property.id}
                                    address={property.addressComplete}
                                    state={property.addressState}
                                    searchType={props.searchType}
                                    searchTab={props.searchTab}
                                    saleData={{
                                        contractDate: property.salesLastSaleContractDate,
                                        type: property.salesLastSaleTypeDescription,
                                        price: property.salesLastSoldPrice,
                                        settlementDate: property.salesLastSaleSettlementDate,
                                        isAgentsAdvice: SearchResultsFunctions
                                            .isRapidAgentAdvice(property.salesLastSaleSource),
                                        isPriceWithheld: property.salesLastSoldPriceIsWithheld
                                    }}
                                    saleCampaign={{
                                        lastListedPrice: property.salesLastCampaignLastListedPriceDescription,
                                        endDate: property.salesLastCampaignEndDate,
                                    }}
                                    rentData={{
                                        price: property.rentalLastCampaignLastListedPrice,
                                        period: property.rentalLastCampaignLastListedPeriod,
                                        date: property.rentalLastCampaignEndDate,
                                    }}
                                    flags={{
                                        isRecentSale: SearchResultsFunctions
                                            .isRecentlySold(property.salesLastSaleContractDate),
                                        isForSaleCampaign: property.isActiveForSaleCampaign,
                                        isForRentCampaign: property.isActiveForRentCampaign,
                                        isHistoric: property.isActive === false,
                                    }}
                                    image={{
                                        src: property.imageUrls?.mediumImageUrl,
                                    }}
                                    attributes={{
                                        landArea: property.landArea,
                                        beds: property.beds,
                                        carSpaces: property.carSpaces,
                                        baths: property.baths,
                                        isCalculatedLandArea: property?.landAreaIsCalculated,
                                    }}
                                />)
                            })
                        }
                    </ScrollableContainer>
                    <BubbleContentSummary size={properties?.length} />
                </>
            </BlockUi>
        </Container>);
};

const DynamicMapContent = (props) => {
    const properties = useSelector(state => state
        .searchResults
        .get('map')
        .get('dynamic')?.properties, shallowEqual);
    const coordinates = useSelector(state => state
        .searchResults
        .get('map')
        .get('dynamic')?.coordinates, shallowEqual);
    const staticPropertySelected = useSelector(state => state
        .searchResults
        .get('map')
        .get('selectedProperty')?.[0], shallowEqual);
    const dynamicSelectedPropertiesFromRedux = useSelector(state => state.searchResults.get('map').get('dynamic')?.selectedProperty, shallowEqual);

    const icon = useMarkerIcon({
        type: 'pending'
    });

    const dynamicSelectedPropertiesIDs = useMemo(() => {
        if (!dynamicSelectedPropertiesFromRedux || !Object.keys(dynamicSelectedPropertiesFromRedux).length) return [];
        return Object.values(dynamicSelectedPropertiesFromRedux).flat().map(prop => prop.id);
    }, [dynamicSelectedPropertiesFromRedux]);

    const { toggleSelection } = useSearchSelectContext();
    const prevSelectedIDsRef = useRef([]);

    useEffect(() => {
        const prevIDs = prevSelectedIDsRef.current;
        const currentIDs = dynamicSelectedPropertiesIDs;

        // If current selection is empty but previous had items, deselect them
        if (!currentIDs.length && prevIDs.length) {
            prevIDs.forEach(id => toggleSelection(id));
        }

        prevSelectedIDsRef.current = currentIDs;
    }, [dynamicSelectedPropertiesIDs, toggleSelection]);

    if (!coordinates?.lat || !coordinates?.lng || staticPropertySelected) {
        return null;
    }

    const markerLabel = properties?.length > 1 ? {
        text: properties.length.toString(),
        fontSize: '10px',
        color: '#FFF',
    } : null;


    return (
        <>
            <Marker
                position={coordinates}
                label={markerLabel}
                icon={icon.get(properties)}
            >
                <InfoBox
                    position={coordinates}
                    options={{
                        boxClass: 'with-tail',
                        pixelOffset: { height: -45, width: -250 },
                        closeBoxURL: '',
                        alignBottom: true,
                        pane: 'floatPane',
                        enableEventPropagation: false,
                        visible: true,
                    }}
                >
                    <>
                        <InfoWindowCloseButton
                            onClick={props.onBubbleClose}
                        />
                        <Content
                            searchType={props.searchType}
                            searchTab={props.searchTab}
                            getLocalityBoundary={props.getLocalityBoundary}
                            getIncludedIds={props.getIncludedIds}
                        />
                    </>
                </InfoBox>
            </Marker>
        </>
    );
};

DynamicMapContent.propTypes = {
    onBubbleClose: PropTypes.func.isRequired,
    searchType: PropTypes.string.isRequired,
    searchTab: PropTypes.string.isRequired,
};

export default DynamicMapContent;
